import React, {Props, useEffect, useRef, useState} from "react";
import ButtonRounded from "../../../components/ButtonRounded";
import Card from "@material-ui/core/Card";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import ModalContainer from "../../../components/ModalContainer";
import moment from "moment";
import {useHistory} from "react-router-dom";
import { getOffRoadUseData, getOffRoadUseCreateData, getOffRoadUseEditData } from "../../../api/sustainability/allocation/offRoadUse/data";
import clsx from "clsx";
import parseAndFormat from "../../../components/texts/parseAndFormat";
import CreatableSelect from "react-select/creatable";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { IconButton } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import parseToFloat from "../../../components/texts/parseToFloat";
import {useOverviewContainerStyles} from "../../../common/styles/mandateOverviewContainerStyles";
import {calculateTotalSurplusOffroad} from "../preliminarySurplus/preliminarySurplusCalculations";
import {resetAllocationMarine} from "../../../api/sustainability/allocation/marineUse/stepData";
import { brandColors } from "../../../theme";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

interface InJointFulfillmentRow {
    company: any;
    orgNumber: any;
    volume: any;
    partAB: any;
}

interface OutJointFulfillmentRow {
    company: any;
    orgNumber: any;
    volume: any;
    partAB: any;
}

interface DieselVol {
    diesel: any;
    biocomponent_in_diesel: any;
    fame: any;
    fame_advanced: any;
    hvo_hro: any;
    hvo_hro_advanced: any;
}

interface DieselBioAboveMandate {
    fame: any;
    fame_advanced: any;
    hvo_hro: any;
    hvo_hro_advanced: any;
}

interface Mandate {
    name: any;
    level: any;
    required_volume: any;
    relevant_volume: any;
}

interface MandateType {
    general_mandate: any;
}

interface DieselExciseDuty {
    diesel: any;
    bio_components_in_diesel: any;
}

interface preli_surplus {
    additional_surplus: any;
    surplus_to_roaduse: any;
}

interface ParamsType {
    id?: any;
    mandate: string;
    diesel_vol_card: {
        sold_vol: {
            diesel: any,
            fame: any,
            fame_advanced: any,
            hvo_hro: any,
            hvo_hro_advanced: any
        },
        bio_above_mandate: {
            fame: any,
            fame_advanced: any,
            hvo_hro: any,
            hvo_hro_advanced: any
        },
        bio_in_fulfillment: {
            "biocomponent_in_diesel": any,
            "fame": any,
            "fame_advanced": any,
            "hvo_hro": any,
            "hvo_hro_advanced": any
        }
    };
    joint_fulfillment_card: {
        in_joint: any,
        out_joint: any
    };
    surplus_card: {
        additional_surplus: any
    };
}

const MARINE_VOLUME_DRAFT_STORAGE_KEY = "sustainability.marineVolumeDraft.v1";

interface MarineVolumeDraft {
    dieselVol: DieselVol;
    dieselBioAboveMandate: DieselBioAboveMandate;
    inJointFulfillmentRows: InJointFulfillmentRow[];
    outJointFulfillmentRows: OutJointFulfillmentRow[];
    inJointAdvancedTotal: number;
    outJointAdvancedTotal: number;
    isJointFulfillmentActive: boolean;
    rawInputs: { [key: string]: string };
    rawBioAboveInputs: { [key: string]: string };
}

const clearMarineVolumeDraftFromSession = () => {
    sessionStorage.removeItem(MARINE_VOLUME_DRAFT_STORAGE_KEY);
};

const readMarineVolumeDraftFromSession = (): MarineVolumeDraft | null => {
    try {
        const raw = sessionStorage.getItem(MARINE_VOLUME_DRAFT_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        return JSON.parse(raw) as MarineVolumeDraft;
    } catch {
        clearMarineVolumeDraftFromSession();
        return null;
    }
};

const useStyles = useOverviewContainerStyles;

const MarineVoumeContainer: React.FC<Props<any>> = () => {
    const classes = useStyles();
    const history = useHistory();
    const persistMarineVolumeDraftRef = useRef<() => void>(() => undefined);
    const [generalMandateStatus , setGeneralMandateStatus] = useState<string>("");
    const [isConfirmVolModalOpen, setIsConfirmVolModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [isShowCalculationModalOpen, setIsShowCalculationModalOpen] = useState(false);
    const [isJointFulfillmentActive, setIsJointFulfillmentActive] = useState(false);
    const [inRowErrors, setInRowErrors] = useState<{ [key: number]: { company?: boolean; orgNumber?: boolean; volume?: boolean; partAB?: boolean } }>({});
    const [outRowErrors, setOutRowErrors] = useState<{ [key: number]: { company?: boolean; orgNumber?: boolean; volume?: boolean; partAB?: boolean } }>({});

    const hasVolumeError = () => {
        const inErrors: { [key: number]: { company?: boolean; orgNumber?: boolean; volume?: boolean; partAB?: boolean } } = {};
        const outErrors: { [key: number]: { company?: boolean; orgNumber?: boolean; volume?: boolean; partAB?: boolean } } = {};

        inJointFulfillmentRows.forEach((row, index) => {
            if (!row.company.trim()) {
                inErrors[index] = { ...inErrors[index], company: true };
            }
            if (!/^\d{9}$/.test(row.orgNumber)) {
                inErrors[index] = { ...inErrors[index], orgNumber: true };
            }
            if (!row.volume.trim()) {
                inErrors[index] = { ...inErrors[index], volume: true };
            }
            if (row.partAB === null) {
                inErrors[index] = { ...inErrors[index], partAB: true };
            }
        });
        setInRowErrors(inErrors);

        outJointFulfillmentRows.forEach((row, index) => {
            if (!row.company.trim()) {
                outErrors[index] = { ...outErrors[index], company: true };
            }
            if (!/^\d{9}$/.test(row.orgNumber)) {
                outErrors[index] = { ...outErrors[index], orgNumber: true };
            }
            if (!row.volume.trim()) {
                outErrors[index] = { ...outErrors[index], volume: true };
            }
            if (row.partAB === null) {
                outErrors[index] = { ...outErrors[index], partAB: true };
            }
        });
        setOutRowErrors(outErrors);

        const hasDieselErrors =
            (dieselVol.fame - dieselBioAboveMandate.fame < 0) ||
            (dieselVol.fame_advanced - dieselBioAboveMandate.fame_advanced < 0) ||
            (dieselVol.hvo_hro - dieselBioAboveMandate.hvo_hro < 0) ||
            (dieselVol.hvo_hro_advanced - dieselBioAboveMandate.hvo_hro_advanced < 0);

        if (Object.keys(inErrors).length > 0 || Object.keys(outErrors).length > 0 || hasDieselErrors) {
            return true;
        }
        return false;
    };

    const handleConfirmVolModalOpen = () => {
        if (hasVolumeError()) {
            setIsErrorModalOpen(true);
            return;
        }
        setIsConfirmVolModalOpen(true);
    };

    const handleCloseConfirmModal = () => {
        setIsConfirmVolModalOpen(false);
    };

    const handleCloseErrorModal = () => {
        setIsErrorModalOpen(false);
    };

    const handleCloseShowCalculationModal = () => {
        setIsShowCalculationModalOpen(false);
    };

    const handleShowCalculationWithSave = () => {
        handleCloseShowCalculationModal();
        if (hasVolumeError()) {
            setIsErrorModalOpen(true);
            return;
        }
        persistMarineVolumeDraftRef.current();
        navigateToCalculationPage();
    };

    const handleShowCalculationWithoutSave = () => {
        handleCloseShowCalculationModal();
        navigateToCalculationPageWithOriginalData();
    };

    const navigateToCalculationPage = () => {
        history.push({
            pathname: "/sustainability/preliminary-surplus-calculation-marine",
            state: {
                generalMandateReqVol: parseFloat(generalMandateReqVol),
                fameAdvBioToBeUsed: parseFloat(dieselVol.fame_advanced) - parseFloat(dieselBioAboveMandate.fame_advanced),
                hvoAdvBioToBeUsed: parseFloat(dieselVol.hvo_hro_advanced) - parseFloat(dieselBioAboveMandate.hvo_hro_advanced),
                fameAdvBioAboveMandate: parseFloat(dieselBioAboveMandate.fame_advanced),
                hvoAdvBioAboveMandate: parseFloat(dieselBioAboveMandate.hvo_hro_advanced),
                fameSoldVol: parseFloat(dieselVol.fame),
                hvoSoldVol: parseFloat(dieselVol.hvo_hro),
                fameAdvSoldVol: parseFloat(dieselVol.fame_advanced),
                hvoAdvSoldVol: parseFloat(dieselVol.hvo_hro_advanced),
                inJointFulfSum: parseToFloat(inJointAdvancedTotal),
                outJointFulfSum: parseToFloat(outJointAdvancedTotal),
                isOffroad: false
            }
        });
    };

    const navigateToCalculationPageWithOriginalData = () => {
        if (originalData) {
            history.push({
                pathname: "/sustainability/preliminary-surplus-calculation-marine",
                state: originalData
            });
        } else {
            navigateToCalculationPage();
        }
    };

    const handleConfirmAndSave = async () => {
        handleCloseConfirmModal();
        const ok = await handleSave();
        if (ok) {
            clearMarineVolumeDraftFromSession();
            history.goBack();
        }
    };

    const handleCancel = () => {
        clearMarineVolumeDraftFromSession();
        history.goBack();
    };
    const [roadUseId, setRoadUseId] = useState<any>(null);
    const [mandate, setMandate] = useState<MandateType>({
        general_mandate: {}
    });
    const [generalMandate, setGeneralMandate] = useState<Mandate>({
        name: "General Mandate",
        level: 0,
        required_volume: 0,
        relevant_volume: 0
    });

    const [dieselExciseDuty, setDieselExciseDuty] = useState<DieselExciseDuty>({
        diesel: 0,
        bio_components_in_diesel: 0
    });

    const [preliSurplus, setPreliSurplus] = useState<preli_surplus>({
        additional_surplus: 0,
        surplus_to_roaduse: 0
    });

    const [inJointAdvancedTotal, setInJointAdvancedTotal] = useState<number>(0);
    const [outJointAdvancedTotal, setOutJointAdvancedTotal] = useState<number>(0);
    const [rawInputs, setRawInputs] = useState<{ [key: string]: string }>({});
    const [rawBioAboveInputs, setRawBioAboveInputs] = useState<{ [key: string]: string }>({});
    const [inJointFulfillmentRows, setInJointFulfillmentRows] = useState<InJointFulfillmentRow[]>([]);
    const [outJointFulfillmentRows, setOutJointFulfillmentRows] = useState<OutJointFulfillmentRow[]>([]);
    const [originalData, setOriginalData] = useState<any>(null);

    const handleShowCalculation = () => {
        setIsShowCalculationModalOpen(true);
    };

    const handleInDeleteRow = (index: number) => {
        setInJointFulfillmentRows(prevRows => {
            const updatedRows = prevRows.filter((_, i) => i !== index);

            let advancedTotal = 0;

            updatedRows.forEach(row => {
                const volume = parseToFloat(row.volume);
                if (!isNaN(volume)) {
                    advancedTotal += volume;
                }
            });

            setInJointAdvancedTotal(advancedTotal);

            return updatedRows;
        });
        setInRowErrors(prevErrors => {
            return Object.fromEntries(
                Object.entries(prevErrors)
                    .filter(([key]) => parseInt(key, 10) !== index) // Remove the error for the deleted row
                    .map(([key, value], newIndex) => [newIndex, value]) // Re-index the remaining errors
            );
        });
    };

    const handleAddInJointRow = () => {
        setInJointFulfillmentRows([
            ...inJointFulfillmentRows,
            { company: "", orgNumber: "", volume: "", partAB: null }
        ]);
    };

    const handleInJointInputChange = (index: number, field: keyof InJointFulfillmentRow, value: string) => {
        const updatedRows = [...inJointFulfillmentRows];
        if (field === "volume") {
            value = value.replace(/[^0-9]/g, "");
            value = parseAndFormat(value, 0, true);
        }
        updatedRows[index][field] = value;
        setInJointFulfillmentRows(updatedRows);
        setInRowErrors(prevErrors => {
            const newErrors = { ...prevErrors };

            if (field === "company" && value.trim()) {
                if (newErrors[index]) {
                    delete newErrors[index].company;
                }
            }

            if (field === "orgNumber" && /^\d{9}$/.test(value)) {
                if (newErrors[index]) {
                    delete newErrors[index].orgNumber;
                }
            }

            if (field === "volume" && value.trim()) {
                if (newErrors[index]) {
                    delete newErrors[index].volume;
                }
            }

            if (field === "partAB" && value != null) {
                if (newErrors[index]) {
                    delete newErrors[index].partAB;
                }
            }

            return newErrors;
        });

        let advancedTotal = 0;

        updatedRows.forEach(row => {
            const volume = parseToFloat(row.volume);
            if (!isNaN(volume)) {
                advancedTotal += volume;
            }
        });

        setInJointAdvancedTotal(advancedTotal);
    };

    const handleOutDeleteRow = (index: number) => {
        setOutJointFulfillmentRows(prevRows => {
            const updatedRows = prevRows.filter((_, i) => i !== index);

            let advancedTotal = 0;

            updatedRows.forEach(row => {
                const volume = parseToFloat(row.volume);
                if (!isNaN(volume)) {
                    advancedTotal += volume;
                }
            });

            setOutJointAdvancedTotal(advancedTotal);

            return updatedRows;
        });
        setOutRowErrors(prevErrors => {
            return Object.fromEntries(
                Object.entries(prevErrors)
                    .filter(([key]) => parseInt(key, 10) !== index) // Remove the error for the deleted row
                    .map(([key, value], newIndex) => [newIndex, value]) // Re-index the remaining errors
            );
        });
    };

    const handleAddOutJointRow = () => {
        setOutJointFulfillmentRows([
            ...outJointFulfillmentRows,
            { company: "", orgNumber: "", volume: "", partAB: null }
        ]);
    };

    const handleOutJointInputChange = (index: number, field: keyof OutJointFulfillmentRow, value: string) => {
        const updatedRows = [...outJointFulfillmentRows];
        if (field === "volume") {
            value = value.replace(/[^0-9]/g, "");
            value = parseAndFormat(value, 0, true);
        }
        updatedRows[index][field] = value;
        setOutJointFulfillmentRows(updatedRows);
        setOutRowErrors(prevErrors => {
            const newErrors = { ...prevErrors };

            if (field === "company" && value.trim()) {
                if (newErrors[index]) {
                    delete newErrors[index].company;
                }
            }

            if (field === "orgNumber" && /^\d{9}$/.test(value)) {
                if (newErrors[index]) {
                    delete newErrors[index].orgNumber;
                }
            }

            if (field === "volume" && value.trim()) {
                if (newErrors[index]) {
                    delete newErrors[index].volume;
                }
            }

            if (field === "partAB" && value != null) {
                if (newErrors[index]) {
                    delete newErrors[index].partAB;
                }
            }

            return newErrors;
        });
        let advancedTotal = 0;

        updatedRows.forEach(row => {
            const volume = parseToFloat(row.volume);
            if (!isNaN(volume)) {
                advancedTotal += volume;
            }
        });

        setOutJointAdvancedTotal(advancedTotal);
    };

    const partABJointOptions = [
        { value: 0, label: "A" },
        { value: 1, label: "B" }
    ];

    const [dieselVol, setDieselVol] = useState<DieselVol>({
        diesel: 0,
        biocomponent_in_diesel: 0,
        fame: 0,
        fame_advanced: 0,
        hvo_hro: 0,
        hvo_hro_advanced: 0
    });

    const [dieselBioAboveMandate, setDieselBioAboveMandate] = useState<DieselBioAboveMandate>({
        fame: 0,
        fame_advanced: 0,
        hvo_hro: 0,
        hvo_hro_advanced: 0
    });

    const handleDieselVol = (fieldName: string) => (e: any) => {
        let inputValue = e.target.value;

        inputValue = inputValue.replace(/[^0-9.,]/g, "").replace(/,/g, ".");

        // Check if the input is a valid number with two decimal places
        if (/^\d+(\.\d{0,2})?$/.test(inputValue) || inputValue === "") {
            const formattedValue = parseAndFormat(inputValue, 2, true);

            setRawInputs(prevRawInputs => ({
                ...prevRawInputs,
                [fieldName]: formattedValue,
            }));
            setDieselVol(prevValues => ({
                ...prevValues,
                [fieldName]: inputValue
            }));
        }

        const biocomponentDieselVol = parseFloat(dieselVol.fame)
            + parseFloat(dieselVol.fame_advanced)
            + parseFloat(dieselVol.hvo_hro)
            + parseFloat(dieselVol.hvo_hro_advanced);

        setDieselVol(prevValues => ({
            ...prevValues,
            ["biocomponent_in_diesel"]: biocomponentDieselVol
        }));
    };

    const handleDieselBioAboveMandate = (fieldName: string) => (e: any) => {
        let inputValue = e.target.value;

        inputValue = inputValue.replace(/[^0-9.,]/g, "").replace(/,/g, ".");

        // Check if the input is a valid number with two decimal places
        if (/^\d+(\.\d{0,2})?$/.test(inputValue) || inputValue === "") {
            const formattedValue = parseAndFormat(inputValue, 2, true);

            setRawBioAboveInputs(prevRawBioAboveInputs => ({
                ...prevRawBioAboveInputs,
                [fieldName]: formattedValue,
            }));

            setDieselBioAboveMandate(prevValues => ({
                ...prevValues,
                [fieldName]: inputValue
            }));
        }
    };

    const fetchData = async () => {
        const response = await getOffRoadUseData("marine");
        if (response && !response.error) {
            if (response.data.id) {
                setRoadUseId(response.data.id);
            }
            if (response.data.preli_mandate_fulfill) {
                setMandate(response.data.preli_mandate_fulfill.mandate);
            }
            if (response.data.preli_calc_surplus_vol) {
                setPreliSurplus(response.data.preli_calc_surplus_vol);
            }
            if (response.data.diesel_sold_vol) {
                setDieselVol(response.data.diesel_sold_vol);
            }
            if (response.data.diesel_bio_above_mandate) {
                setDieselBioAboveMandate(response.data.diesel_bio_above_mandate);
            }
            if (response.data.diesel_excise_duty) {
                setDieselExciseDuty(response.data.diesel_excise_duty);
            }
            if (response.data.joint_fulfillment) {
                setIsJointFulfillmentActive(response.data.joint_fulfillment.is_active);
                const inJointRows = response.data.joint_fulfillment.in_joint.map((row: InJointFulfillmentRow) => ({
                    ...row,
                    volume: parseAndFormat(row.volume, 0, true),
                }));

                const outJointRows = response.data.joint_fulfillment.out_joint.map((row: OutJointFulfillmentRow) => ({
                    ...row,
                    volume: parseAndFormat(row.volume, 0, true),
                }));
                if (inJointRows.length > 0) {
                    setInJointFulfillmentRows(inJointRows);
                }
                if (outJointRows.length > 0) {
                    setOutJointFulfillmentRows(outJointRows);
                }
                setInJointAdvancedTotal(response.data.joint_fulfillment.in_joint_advanced_total);
                setOutJointAdvancedTotal(response.data.joint_fulfillment.out_joint_advanced_total);
                const originalDataObj = {
                    generalMandateReqVol: parseFloat(calculateGeneralReqVol(response.data.diesel_sold_vol, response.data.preli_mandate_fulfill.mandate)),
                    fameAdvBioToBeUsed: parseFloat(response.data.diesel_sold_vol.fame_advanced) - parseFloat(response.data.diesel_bio_above_mandate.fame_advanced),
                    hvoAdvBioToBeUsed: parseFloat(response.data.diesel_sold_vol.hvo_hro_advanced) - parseFloat(response.data.diesel_bio_above_mandate.hvo_hro_advanced),
                    fameAdvBioAboveMandate: parseFloat(response.data.diesel_bio_above_mandate.fame_advanced),
                    hvoAdvBioAboveMandate: parseFloat(response.data.diesel_bio_above_mandate.hvo_hro_advanced),
                    fameSoldVol: parseFloat(response.data.diesel_sold_vol.fame),
                    hvoSoldVol: parseFloat(response.data.diesel_sold_vol.hvo_hro),
                    fameAdvSoldVol: parseFloat(response.data.diesel_sold_vol.fame_advanced),
                    hvoAdvSoldVol: parseFloat(response.data.diesel_sold_vol.hvo_hro_advanced),
                    inJointFulfSum: parseToFloat(response.data.joint_fulfillment.in_joint_advanced_total),
                    outJointFulfSum: parseToFloat(response.data.joint_fulfillment.out_joint_advanced_total),
                    isOffroad: false
                };
                setOriginalData(originalDataObj);
            }
            const draft = readMarineVolumeDraftFromSession();
            if (draft) {
                setDieselVol(draft.dieselVol);
                setDieselBioAboveMandate(draft.dieselBioAboveMandate);
                setInJointFulfillmentRows(draft.inJointFulfillmentRows);
                setOutJointFulfillmentRows(draft.outJointFulfillmentRows);
                setInJointAdvancedTotal(draft.inJointAdvancedTotal);
                setOutJointAdvancedTotal(draft.outJointAdvancedTotal);
                setIsJointFulfillmentActive(draft.isJointFulfillmentActive);
                setRawInputs(draft.rawInputs || {});
                setRawBioAboveInputs(draft.rawBioAboveInputs || {});
            }
        } else {
            alert("Some error occured");
        }
    };

    const handleSave = async (): Promise<boolean> => {
        const params: ParamsType = {
            mandate: "marine",
            diesel_vol_card: {
                sold_vol: {
                    diesel: dieselVol.diesel,
                    fame: dieselVol.fame,
                    fame_advanced: dieselVol.fame_advanced,
                    hvo_hro: dieselVol.hvo_hro,
                    hvo_hro_advanced: dieselVol.hvo_hro_advanced
                },
                bio_above_mandate: {
                    fame: dieselBioAboveMandate.fame,
                    fame_advanced: dieselBioAboveMandate.fame_advanced,
                    hvo_hro: dieselBioAboveMandate.hvo_hro,
                    hvo_hro_advanced: dieselBioAboveMandate.hvo_hro_advanced
                },
                bio_in_fulfillment: {
                    biocomponent_in_diesel: (
                            parseFloat(dieselVol.fame)
                            + parseFloat(dieselVol.fame_advanced)
                            + parseFloat(dieselVol.hvo_hro)
                            + parseFloat(dieselVol.hvo_hro_advanced)
                        ) -
                        (
                            parseFloat(dieselBioAboveMandate.fame)
                            + parseFloat(dieselBioAboveMandate.fame_advanced)
                            + parseFloat(dieselBioAboveMandate.hvo_hro)
                            + parseFloat(dieselBioAboveMandate.hvo_hro_advanced)
                        ),
                    fame: parseFloat(dieselVol.fame) - parseFloat(dieselBioAboveMandate.fame),
                    fame_advanced: parseFloat(dieselVol.fame_advanced) - parseFloat(dieselBioAboveMandate.fame_advanced),
                    hvo_hro: parseFloat(dieselVol.hvo_hro) - parseFloat(dieselBioAboveMandate.hvo_hro),
                    hvo_hro_advanced: parseFloat(dieselVol.hvo_hro_advanced) - parseFloat(dieselBioAboveMandate.hvo_hro_advanced)
                }
            },
            joint_fulfillment_card: {
                in_joint: inJointFulfillmentRows.map(row => ({
                    ...row,
                    volume: row.volume === "" ? 0 : parseToFloat(row.volume),
                })),
                out_joint: outJointFulfillmentRows.map(row => ({
                    ...row,
                    volume: row.volume === "" ? 0 : parseToFloat(row.volume),
                }))
            },
            surplus_card: {
                additional_surplus: preliSurplus.additional_surplus,
            },
        };

        if (roadUseId) {
            params.id = roadUseId;
            const response = await getOffRoadUseEditData(params);
            if (!response || response.error) {
                alert("Some error occured");
                return false;
            }
        } else {
            const response2 = await getOffRoadUseCreateData(params);
            if (!response2 || response2.error) {
                alert("Some error occured");
                return false;
            }
        }
        await resetAllocationMarine();
        return true;

    };

    const calculateGeneralReqVol = (dieselVol: DieselVol, mandate: MandateType) => {
        return (( parseFloat(dieselVol.fame || 0)
            + parseFloat(dieselVol.fame_advanced || 0)
            + parseFloat(dieselVol.hvo_hro || 0)
            + parseFloat(dieselVol.hvo_hro_advanced || 0)
            + parseFloat(dieselVol.diesel || 0)
        ) * (parseFloat(mandate.general_mandate.level) / 100)).toFixed(2);
    };

    const generalMandateReqVol = calculateGeneralReqVol(dieselVol, mandate);

    const generalMandateRelVol = (
        (
            parseFloat(dieselVol.fame || 0)
            + parseFloat(dieselVol.fame_advanced || 0)
            + parseFloat(dieselVol.hvo_hro || 0)
            + parseFloat(dieselVol.hvo_hro_advanced || 0)
        ) -
        (
            parseFloat(dieselBioAboveMandate.fame || 0)
            + parseFloat(dieselBioAboveMandate.fame_advanced || 0)
            + parseFloat(dieselBioAboveMandate.hvo_hro || 0)
            + parseFloat(dieselBioAboveMandate.hvo_hro_advanced || 0)
        ) +
        parseToFloat(inJointAdvancedTotal)
    ).toFixed(2);

    const biocomponentDiesel = (
        (
            parseFloat(dieselVol.fame || 0)
            + parseFloat(dieselVol.fame_advanced || 0)
            + parseFloat(dieselVol.hvo_hro || 0)
            + parseFloat(dieselVol.hvo_hro_advanced || 0)
        ) -
        (
            parseFloat(dieselBioAboveMandate.fame || 0)
            + parseFloat(dieselBioAboveMandate.fame_advanced || 0)
            + parseFloat(dieselBioAboveMandate.hvo_hro || 0)
            + parseFloat(dieselBioAboveMandate.hvo_hro_advanced || 0)
        )
    ).toFixed(2);

    persistMarineVolumeDraftRef.current = () => {
        try {
            const snapshot: MarineVolumeDraft = {
                dieselVol,
                dieselBioAboveMandate,
                inJointFulfillmentRows,
                outJointFulfillmentRows,
                inJointAdvancedTotal,
                outJointAdvancedTotal,
                isJointFulfillmentActive,
                rawInputs,
                rawBioAboveInputs,
            };
            sessionStorage.setItem(MARINE_VOLUME_DRAFT_STORAGE_KEY, JSON.stringify(snapshot));
        } catch {
            // ignore quota / private mode
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const { total_surplus_available, mandateStatus } = calculateTotalSurplusOffroad({
            generalMandateReqVol: parseFloat(generalMandateReqVol) || 0,
            fameAdvBioToBeUsed: parseFloat(dieselVol.fame_advanced || 0) - parseFloat(dieselBioAboveMandate.fame_advanced || 0),
            hvoAdvBioToBeUsed: parseFloat(dieselVol.hvo_hro_advanced || 0) - parseFloat(dieselBioAboveMandate.hvo_hro_advanced || 0),
            fameAdvBioAboveMandate: parseFloat(dieselBioAboveMandate.fame_advanced || 0),
            hvoAdvBioAboveMandate: parseFloat(dieselBioAboveMandate.hvo_hro_advanced || 0),
            fameAdvSoldVol: parseFloat(dieselVol.fame_advanced || 0),
            hvoAdvSoldVol: parseFloat(dieselVol.hvo_hro_advanced || 0),
            inJointFulfSum: parseToFloat(inJointAdvancedTotal),
            outJointFulfSum: parseToFloat(outJointAdvancedTotal)
        });

        setPreliSurplus(prev => ({
            ...prev,
            additional_surplus: total_surplus_available
        }));

        setGeneralMandateStatus(mandateStatus);

    }, [generalMandateReqVol, dieselVol, dieselBioAboveMandate, inJointAdvancedTotal, outJointAdvancedTotal]);

    useEffect(() => {
        setGeneralMandate(mandate.general_mandate);
    }, [mandate]);

    return (
        <div>
            <div className={classes.container}>
                <div>
                    <h1><span style={{ color: brandColors.primary.main }}>MARINE</span> volumes</h1>
                </div>
                <div className={classes.buttonContainer}>
                    <ButtonRounded className={classes.buttonMargin}
                                   fullWidth
                                   variant="contained"
                                   color="primary"
                                   onClick={handleConfirmVolModalOpen}
                    >
                        Save
                    </ButtonRounded>
                    <ButtonRounded className={classes.buttonMargin}
                                   fullWidth
                                   variant="outlined"
                                   color="default"
                                   onClick={handleCancel}
                    >
                        Cancel
                    </ButtonRounded>
                    {isErrorModalOpen && (
                        <ModalContainer
                            open={isErrorModalOpen}
                            onClose={handleCloseErrorModal}
                            title="Error!"
                            maxWidth="xs"
                        >
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: "16px",
                                }}
                            >
                                <Typography variant="body1" gutterBottom>
                                    There are some errors in this page. Kindly check and correct them before saving.
                                </Typography>
                            </div>
                        </ModalContainer>
                    )}
                    {isConfirmVolModalOpen && (
                        <ModalContainer
                            open={isConfirmVolModalOpen}
                            onClose={handleCloseConfirmModal}
                            title="Save and reset allocation?"
                            maxWidth="sm"
                        >
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: "16px",
                                }}
                            >
                                <Typography variant="body1" gutterBottom>
                                    Saving these volumes will reset allocation progress for this mandate. This cannot be undone.
                                </Typography>
                                <Grid
                                    container
                                    spacing={2}
                                    alignContent={"center"}
                                    alignItems={"center"}
                                    justify={"center"}
                                >
                                    <Grid item>
                                        <br></br>
                                        <ButtonRounded
                                            variant="outlined"
                                            color="default"
                                            onClick={handleCloseConfirmModal}
                                        >
                                            Cancel
                                        </ButtonRounded>
                                    </Grid>
                                    <Grid item>
                                        <br></br>
                                        <ButtonRounded
                                            variant="contained"
                                            color="primary"
                                            onClick={handleConfirmAndSave}
                                        >
                                            Save and reset
                                        </ButtonRounded>
                                    </Grid>
                                </Grid>
                            </div>
                        </ModalContainer>
                    )}
                    {isShowCalculationModalOpen && (
                        <ModalContainer
                            open={isShowCalculationModalOpen}
                            onClose={handleCloseShowCalculationModal}
                            title="Show calculation"
                            maxWidth="sm"
                        >
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: "16px",
                                }}
                            >
                                <Typography variant="body1">
                                    Calculate using your current edits, or the last saved values?
                                </Typography>
                                <div className={classes.infoBox}>
                                    <InfoOutlinedIcon className={classes.infoBoxIcon} fontSize="small" />
                                    <Typography variant="body2" className={classes.infoBoxText}>
                                        Your edits stay in this session until you Save at the top of the page. Saving will reset allocation progress for this mandate.
                                    </Typography>
                                </div>
                                <Grid
                                    container
                                    spacing={2}
                                    alignContent={"center"}
                                    alignItems={"center"}
                                    justify={"center"}
                                    style={{ marginTop: "16px" }}
                                >
                                    <Grid item>
                                        <ButtonRounded className={classes.buttonMargin}
                                                       fullWidth
                                                       variant="contained"
                                                       color="primary"
                                                       onClick={handleShowCalculationWithSave}
                                        >
                                            Use my edits
                                        </ButtonRounded>
                                    </Grid>
                                    <Grid item>
                                        <ButtonRounded className={classes.buttonMargin}
                                                       fullWidth
                                                       variant="outlined"
                                                       color="default"
                                                       onClick={handleShowCalculationWithoutSave}
                                        >
                                            Use saved values
                                        </ButtonRounded>
                                    </Grid>
                                </Grid>
                            </div>
                        </ModalContainer>
                    )}
                </div>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Preliminary mandate fulfillment</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell>Mandate description</TableCell>
                                <TableCell>Mandate level</TableCell>
                                <TableCell>Required volume</TableCell>
                                <TableCell>Estimated status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "20%"}}>General mandate</TableCell>
                                <TableCell style={{width: "20%"}}>{ mandate.general_mandate.level }%</TableCell>
                                <TableCell style={{width: "20%"}}>
                                    {parseAndFormat(generalMandateReqVol, 0)}
                                </TableCell>
                                <TableCell style={{width: "20%"}} className={clsx({
                                    [classes.green]: generalMandateStatus === "OK Mandate fulfilled + surplus LOCKED",
                                    [classes.yellow]: generalMandateStatus === "ERROR Mandate fulfilled, but not surplus LOCKED",
                                    [classes.red]: generalMandateStatus === "ERROR Mandate not fulfilled",
                                })}>
                                    {generalMandateStatus}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Preliminary calculations of surplus volume</h3>
                <Grid container alignItems="center" justify="space-between" style={{ marginBottom: 16 }}>
                    <Grid item xs>
                        <p style={{ margin: 0, marginRight: 50 }}>
                            This section contains a <strong>preliminary</strong> and <strong>estimated</strong> calculation of surplus volumes for the marine
                            mandate. <strong> NB!</strong> The output in terms of the actual surplus can be different when the allocation process has been completed.
                        </p>
                    </Grid>
                    <Grid item>
                        <ButtonRounded
                            className={classes.buttonMargin}
                            fullWidth={false}
                            variant="contained"
                            color="primary"
                            onClick={handleShowCalculation}
                        >
                            Show calculation
                        </ButtonRounded>
                    </Grid>
                </Grid>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Surplus description</TableCell>
                                <TableCell>Required by customers</TableCell>
                                <TableCell>Est. additional surplus</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell style={{width: "25%"}}>Bio component in diesel</TableCell>
                                <TableCell style={{width: "25%"}}>
                                    {
                                        parseAndFormat(parseFloat(dieselBioAboveMandate.fame || 0)
                                        + parseFloat(dieselBioAboveMandate.fame_advanced || 0)
                                        + parseFloat(dieselBioAboveMandate.hvo_hro || 0)
                                        + parseFloat(dieselBioAboveMandate.hvo_hro_advanced || 0), 0)
                                    }
                                </TableCell>
                                <TableCell style={{width: "25%"}}>{ parseAndFormat(preliSurplus.additional_surplus, 0) }</TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{width: "25%"}}>Total surplus</TableCell>
                                <TableCell style={{width: "25%"}}>
                                    {
                                        parseAndFormat(parseFloat(dieselBioAboveMandate.fame || 0)
                                            + parseFloat(dieselBioAboveMandate.fame_advanced || 0)
                                            + parseFloat(dieselBioAboveMandate.hvo_hro || 0)
                                            + parseFloat(dieselBioAboveMandate.hvo_hro_advanced || 0), 0)
                                    }
                                </TableCell>
                                <TableCell style={{width: "25%"}}>{ parseAndFormat(preliSurplus.additional_surplus, 0) }</TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            {isJointFulfillmentActive && (
                <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                    <h3>Joint fuflillment IN</h3>
                    <Card className={classes.card}>
                        <Table className={classes.table}>
                            {inJointFulfillmentRows.length > 0 &&
                                <>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{width: "30%"}}>Company name</TableCell>
                                            <TableCell style={{width: "25%"}}>Org. number</TableCell>
                                            <TableCell style={{width: "25%"}}>Volume</TableCell>
                                            <TableCell style={{width: "15%"}}>Part A/B</TableCell>
                                            <TableCell style={{width: "5%"}}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {inJointFulfillmentRows.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell style={{ width: "30%" }}>
                                                    <TextField
                                                        label=""
                                                        type="text"
                                                        name="company"
                                                        variant="outlined"
                                                        value={row.company}
                                                        onChange={e =>
                                                            handleInJointInputChange(index, "company", e.target.value)}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!(inRowErrors[index] && inRowErrors[index].company)}
                                                        helperText={inRowErrors[index] && inRowErrors[index].company ? "Company name can't be empty" : ""}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ width: "25%" }}>
                                                    <TextField
                                                        label=""
                                                        type="text"
                                                        name="orgNumber"
                                                        variant="outlined"
                                                        value={row.orgNumber}
                                                        onChange={e =>
                                                            handleInJointInputChange(index, "orgNumber", e.target.value)}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!(inRowErrors[index] && inRowErrors[index].orgNumber)}
                                                        helperText={inRowErrors[index] && inRowErrors[index].orgNumber ? "Org. Number must be of 9 digits" : ""}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ width: "25%" }}>
                                                    <TextField
                                                        label=""
                                                        type="text"
                                                        name="volume"
                                                        variant="outlined"
                                                        value={row.volume}
                                                        onChange={e =>
                                                            handleInJointInputChange(index, "volume", e.target.value)}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!(inRowErrors[index] && inRowErrors[index].volume)}
                                                        helperText={inRowErrors[index] && inRowErrors[index].volume ? "Volume can't be empty" : ""}
                                                    />
                                                </TableCell>

                                                <TableCell style={{ width: "15%" }}>
                                                    <div>
                                                        <CreatableSelect
                                                            key={index}
                                                            isDisabled={false}
                                                            className={classes.creatableSelect}
                                                            classNamePrefix="react-select"
                                                            placeholder=""
                                                            components={{
                                                                DropdownIndicator: () => <ArrowDropDownIcon />,
                                                            }}
                                                            options={partABJointOptions}
                                                            value={row.partAB !== null ? partABJointOptions.find(option => option.value === row.partAB) : null}
                                                            onChange={(selected: any) => {
                                                                handleInJointInputChange(index, "partAB", selected ? selected.value : null);
                                                            }}
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                menuPortal: base => ({ ...base, zIndex: 99999 }),
                                                                control: base => ({
                                                                    ...base,
                                                                    borderColor: inRowErrors[index] && inRowErrors[index].partAB ? '#f44336 !important' : base.borderColor
                                                                })
                                                            }}
                                                        />
                                                        {inRowErrors[index] && inRowErrors[index].partAB && (
                                                            <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                                                                Please select an option
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell style={{ width: "5%" }}>
                                                    <IconButton onClick={() => handleInDeleteRow(index)} color="secondary">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell style={{width: "30%"}}></TableCell>
                                            <TableCell style={{width: "25%"}}></TableCell>
                                            <TableCell style={{width: "25%"}}></TableCell>
                                            <TableCell style={{width: "15%"}}></TableCell>
                                            <TableCell style={{width: "5%"}}></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </>
                            }
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        onClick={handleAddInJointRow}
                                        style={{
                                            cursor: "pointer",
                                            width: "30%",
                                            color: "#424B5A",
                                            transition: "color 0.3s ease"
                                        }}
                                        onMouseEnter={(e: React.MouseEvent<HTMLTableCellElement>) => {
                                            e.currentTarget.style.color = brandColors.primary.main;
                                        }}
                                        onMouseLeave={(e: React.MouseEvent<HTMLTableCellElement>) => {
                                            e.currentTarget.style.color = "#424B5A";
                                        }}
                                    >
                                        + Add new joint fulfillment IN
                                    </TableCell>
                                    <TableCell style={{width: "25%"}}></TableCell>
                                    <TableCell style={{width: "25%"}}></TableCell>
                                    <TableCell style={{width: "15%"}}></TableCell>
                                    <TableCell style={{width: "5%"}}></TableCell>
                                </TableRow>
                            </TableHead>
                        </Table>
                    </Card>
                </div>
            )}

            {isJointFulfillmentActive && (
                <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                    <h3>Joint fuflillment OUT</h3>
                    <Card className={classes.card}>
                        <Table className={classes.table}>
                            {outJointFulfillmentRows.length > 0 &&
                                <>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{width: "30%"}}>Company name</TableCell>
                                            <TableCell style={{width: "25%"}}>Org. number</TableCell>
                                            <TableCell style={{width: "25%"}}>Volume</TableCell>
                                            <TableCell style={{width: "15%"}}>Part A/B</TableCell>
                                            <TableCell style={{width: "5%"}}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {outJointFulfillmentRows.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell style={{ width: "30%" }}>
                                                    <TextField
                                                        label=""
                                                        type="text"
                                                        name="company"
                                                        variant="outlined"
                                                        value={row.company}
                                                        onChange={e =>
                                                            handleOutJointInputChange(index, "company", e.target.value)}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!(outRowErrors[index] && outRowErrors[index].company)}
                                                        helperText={outRowErrors[index] && outRowErrors[index].company ? "Company name can't be empty" : ""}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ width: "25%" }}>
                                                    <TextField
                                                        label=""
                                                        type="text"
                                                        name="orgNumber"
                                                        variant="outlined"
                                                        value={row.orgNumber}
                                                        onChange={e =>
                                                            handleOutJointInputChange(index, "orgNumber", e.target.value)}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!(outRowErrors[index] && outRowErrors[index].orgNumber)}
                                                        helperText={outRowErrors[index] && outRowErrors[index].orgNumber ? "Org. Number must be of 9 digits" : ""}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ width: "25%" }}>
                                                    <TextField
                                                        label=""
                                                        type="text"
                                                        name="volume"
                                                        variant="outlined"
                                                        value={row.volume}
                                                        onChange={e =>
                                                            handleOutJointInputChange(index, "volume", e.target.value)}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!(outRowErrors[index] && outRowErrors[index].volume)}
                                                        helperText={outRowErrors[index] && outRowErrors[index].volume ? "Volume can't be empty" : ""}
                                                    />
                                                </TableCell>

                                                <TableCell style={{ width: "15%" }}>
                                                    <div>
                                                        <CreatableSelect
                                                            key={index}
                                                            isDisabled={false}
                                                            className={classes.creatableSelect}
                                                            classNamePrefix="react-select"
                                                            placeholder=""
                                                            components={{
                                                                DropdownIndicator: () => <ArrowDropDownIcon />,
                                                            }}
                                                            options={partABJointOptions}
                                                            value={row.partAB !== null ? partABJointOptions.find(option => option.value === row.partAB) : null}
                                                            onChange={(selected: any) => {
                                                                handleOutJointInputChange(index, "partAB", selected ? selected.value : null);
                                                            }}
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                menuPortal: base => ({ ...base, zIndex: 99999 }),
                                                                control: base => ({
                                                                    ...base,
                                                                    borderColor: outRowErrors[index] && outRowErrors[index].partAB ? '#f44336 !important' : base.borderColor
                                                                })
                                                            }}
                                                        />
                                                        {outRowErrors[index] && outRowErrors[index].partAB && (
                                                            <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                                                                Please select an option
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell style={{ width: "5%" }}>
                                                    <IconButton onClick={() => handleOutDeleteRow(index)} color="secondary">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell style={{width: "30%"}}></TableCell>
                                            <TableCell style={{width: "25%"}}></TableCell>
                                            <TableCell style={{width: "25%"}}></TableCell>
                                            <TableCell style={{width: "15%"}}></TableCell>
                                            <TableCell style={{width: "5%"}}></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </>
                            }
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        onClick={handleAddOutJointRow}
                                        style={{
                                            cursor: "pointer",
                                            width: "30%",
                                            color: "#424B5A",
                                            transition: "color 0.3s ease"
                                        }}
                                        onMouseEnter={(e: React.MouseEvent<HTMLTableCellElement>) => {
                                            e.currentTarget.style.color = brandColors.primary.main;
                                        }}
                                        onMouseLeave={(e: React.MouseEvent<HTMLTableCellElement>) => {
                                            e.currentTarget.style.color = "#424B5A";
                                        }}
                                    >
                                        + Add new joint fulfillment OUT
                                    </TableCell>
                                    <TableCell style={{width: "25%"}}></TableCell>
                                    <TableCell style={{width: "25%"}}></TableCell>
                                    <TableCell style={{width: "15%"}}></TableCell>
                                    <TableCell style={{width: "5%"}}></TableCell>
                                </TableRow>
                            </TableHead>
                        </Table>
                    </Card>
                </div>
            )}

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Diesel volumes</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Product component</TableCell>
                                <TableCell>Excise duty volume</TableCell>
                                <TableCell>Sold volume</TableCell>
                                <TableCell>Bio above mandate</TableCell>
                                <TableCell>Bio to be used in fulf.</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell style={{width: "20%"}}>Diesel</TableCell>
                                <TableCell style={{width: "20%"}}>{dieselExciseDuty.diesel}</TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        name="diesel"
                                        value={rawInputs["diesel"] || parseAndFormat(dieselVol.diesel, 2)}
                                        variant="outlined"
                                        onChange={handleDieselVol("diesel")}
                                        fullWidth
                                        margin="normal"
                                    />
                                </TableCell>
                                <TableCell style={{width: "20%"}}>N/A</TableCell>
                                <TableCell style={{width: "20%"}}>N/A</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "20%", borderBottom: "1px solid black"}}>Bio component in Diesel</TableCell>
                                <TableCell style={{width: "20%", borderBottom: "1px solid black"}}>
                                    {parseAndFormat(dieselExciseDuty.bio_components_in_diesel, 2)}
                                </TableCell>
                                <TableCell style={{width: "20%", borderBottom: "1px solid black"}}>
                                    {
                                        parseAndFormat(parseFloat(dieselVol.fame || 0)
                                        + parseFloat(dieselVol.fame_advanced || 0)
                                        + parseFloat(dieselVol.hvo_hro || 0)
                                        + parseFloat(dieselVol.hvo_hro_advanced || 0), 2)
                                    }
                                </TableCell>
                                <TableCell style={{width: "20%", borderBottom: "1px solid black"}}>
                                    {
                                        parseAndFormat(parseFloat(dieselBioAboveMandate.fame || 0)
                                        + parseFloat(dieselBioAboveMandate.fame_advanced || 0)
                                        + parseFloat(dieselBioAboveMandate.hvo_hro || 0)
                                        + parseFloat(dieselBioAboveMandate.hvo_hro_advanced || 0), 2)
                                    }
                                </TableCell>
                                <TableCell style={{width: "20%", borderBottom: "1px solid black"}}>
                                    {
                                        parseAndFormat(
                                            (
                                                parseFloat(dieselVol.fame_advanced || 0)
                                                + parseFloat(dieselVol.hvo_hro_advanced || 0)
                                            ) -
                                            (
                                                parseFloat(dieselBioAboveMandate.fame_advanced || 0)
                                                + parseFloat(dieselBioAboveMandate.hvo_hro_advanced || 0)
                                            ), 2)

                                    }
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "20%"}}>FAME</TableCell>
                                <TableCell style={{width: "20%"}}></TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        name="fame"
                                        value={rawInputs["fame"] || parseAndFormat(dieselVol.fame, 2)}
                                        variant="outlined"
                                        onChange={handleDieselVol("fame")}
                                        fullWidth
                                        margin="normal"
                                    />
                                </TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        name="above_fame"
                                        value={rawBioAboveInputs["fame"] || parseAndFormat(dieselBioAboveMandate.fame, 2)}
                                        variant="outlined"
                                        onChange={handleDieselBioAboveMandate("fame")}
                                        fullWidth
                                        margin="normal"
                                        disabled={true}
                                    />
                                </TableCell>
                                <TableCell style={{width: "20%"}}>
                                    N/A
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "20%"}}>FAME (Advanced)</TableCell>
                                <TableCell style={{width: "20%"}}></TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        name="fame_advanced"
                                        value={rawInputs["fame_advanced"] || parseAndFormat(dieselVol.fame_advanced, 2)}
                                        variant="outlined"
                                        onChange={handleDieselVol("fame_advanced")}
                                        fullWidth
                                        margin="normal"
                                        error={dieselVol.fame_advanced - dieselBioAboveMandate.fame_advanced < 0}
                                    />
                                </TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        name="above_fame_advanced"
                                        value={rawBioAboveInputs["fame_advanced"] || parseAndFormat(dieselBioAboveMandate.fame_advanced, 2)}
                                        variant="outlined"
                                        onChange={handleDieselBioAboveMandate("fame_advanced")}
                                        fullWidth
                                        margin="normal"
                                        error={dieselVol.fame_advanced - dieselBioAboveMandate.fame_advanced < 0}
                                    />
                                </TableCell>
                                <TableCell style={{
                                    width: "20%",
                                    color: dieselVol.fame_advanced - dieselBioAboveMandate.fame_advanced < 0 ? '#f44336' : undefined
                                }}>
                                    {
                                        parseAndFormat(dieselVol.fame_advanced - dieselBioAboveMandate.fame_advanced, 2)
                                    }
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "20%"}}>HVO/HRO</TableCell>
                                <TableCell style={{width: "20%"}}></TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        value={rawInputs["hvo_hro"] || parseAndFormat(dieselVol.hvo_hro, 2)}
                                        variant="outlined"
                                        onChange={handleDieselVol("hvo_hro")}
                                        fullWidth
                                        margin="normal"
                                    />
                                </TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        value={rawBioAboveInputs["hvo_hro"] || parseAndFormat(dieselBioAboveMandate.hvo_hro, 2)}
                                        variant="outlined"
                                        onChange={handleDieselBioAboveMandate("hvo_hro")}
                                        fullWidth
                                        margin="normal"
                                        disabled={true}
                                    />
                                </TableCell>
                                <TableCell style={{width: "20%"}}>
                                    N/A
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "20%"}}>HVO/HRO (Advanced)</TableCell>
                                <TableCell style={{width: "20%"}}></TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        value={rawInputs["hvo_hro_advanced"] || parseAndFormat(dieselVol.hvo_hro_advanced, 2)}
                                        variant="outlined"
                                        onChange={handleDieselVol("hvo_hro_advanced")}
                                        fullWidth
                                        margin="normal"
                                        error={dieselVol.hvo_hro_advanced - dieselBioAboveMandate.hvo_hro_advanced < 0}
                                    />
                                </TableCell>
                                <TableCell style={{width: "20%"}}>
                                    <TextField
                                        label=""
                                        type="text"
                                        value={rawBioAboveInputs["hvo_hro_advanced"] || parseAndFormat(dieselBioAboveMandate.hvo_hro_advanced, 2)}
                                        variant="outlined"
                                        onChange={handleDieselBioAboveMandate("hvo_hro_advanced")}
                                        fullWidth
                                        margin="normal"
                                        error={dieselVol.hvo_hro_advanced - dieselBioAboveMandate.hvo_hro_advanced < 0}
                                    />
                                </TableCell>
                                <TableCell style={{
                                    width: "20%",
                                    color: dieselVol.hvo_hro_advanced - dieselBioAboveMandate.hvo_hro_advanced < 0 ? '#f44336' : undefined
                                }}>
                                    {
                                        parseAndFormat(dieselVol.hvo_hro_advanced - dieselBioAboveMandate.hvo_hro_advanced, 2)
                                    }
                                </TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead>
                            <TableRow>
                                <TableCell>Total sold diesel volumes</TableCell>
                                <TableCell></TableCell>
                                <TableCell>
                                    {
                                        parseAndFormat(parseFloat(dieselVol.fame || 0)
                                        + parseFloat(dieselVol.fame_advanced || 0)
                                        + parseFloat(dieselVol.hvo_hro || 0)
                                        + parseFloat(dieselVol.hvo_hro_advanced || 0)
                                        + parseFloat(dieselVol.diesel || 0), 2)
                                    }
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>
        </div>
    );
};

export default MarineVoumeContainer;
